import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { getMission, getMissionBids, acceptBid, rejectBid, createBid, getVehicles, createCheckoutSession, createRating, getMissionRatings, updateMissionStatus } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Truck,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
  CreditCard,
  Star,
  Euro
} from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MissionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isEnterprise, isCarrier } = useAuth();
  const [mission, setMission] = useState(null);
  const [bids, setBids] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [bidForm, setBidForm] = useState({
    vehicle_id: '',
    proposed_price: '',
    estimated_delivery: '',
    message: ''
  });
  const [ratingForm, setRatingForm] = useState({
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    fetchMissionData();
  }, [id]);

  const fetchMissionData = async () => {
    try {
      const missionData = await getMission(id);
      setMission(missionData);

      const bidsData = await getMissionBids(id);
      setBids(bidsData);

      const ratingsData = await getMissionRatings(id);
      setRatings(ratingsData);

      if (isCarrier) {
        const vehiclesData = await getVehicles();
        setVehicles(vehiclesData);
      }
    } catch (error) {
      console.error('Failed to fetch mission:', error);
      toast.error('Impossible de charger la mission');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      const result = await acceptBid(bidId);
      toast.success(`Offre acceptée ! Commission plateforme: ${result.platform_fee?.toFixed(2)}€`);
      fetchMissionData();
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation');
    }
  };

  const handleRejectBid = async (bidId) => {
    try {
      await rejectBid(bidId);
      toast.success('Offre refusée');
      fetchMissionData();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setSubmittingBid(true);

    try {
      await createBid({
        mission_id: id,
        vehicle_id: bidForm.vehicle_id,
        proposed_price: parseFloat(bidForm.proposed_price),
        estimated_delivery: bidForm.estimated_delivery,
        message: bidForm.message
      });
      toast.success('Offre soumise avec succès !');
      setBidDialogOpen(false);
      fetchMissionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmittingBid(false);
    }
  };

  const handlePayment = async () => {
    setProcessingPayment(true);
    try {
      const { url } = await createCheckoutSession(id);
      window.location.href = url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setProcessingPayment(false);
    }
  };

  const handleCompleteMission = async () => {
    try {
      await updateMissionStatus(id, 'completed');
      toast.success('Mission marquée comme terminée');
      fetchMissionData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    setSubmittingRating(true);

    try {
      const ratedUserId = isEnterprise ? mission.assigned_carrier_id : mission.enterprise_id;
      await createRating({
        mission_id: id,
        rated_user_id: ratedUserId,
        rating: ratingForm.rating,
        comment: ratingForm.comment
      });
      toast.success('Évaluation envoyée !');
      setRatingDialogOpen(false);
      fetchMissionData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'évaluation');
    } finally {
      setSubmittingRating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'status-pending' },
      matched: { label: 'Attribué', className: 'status-matched' },
      in_progress: { label: 'En cours', className: 'status-in_progress' },
      completed: { label: 'Terminé', className: 'status-completed' },
      cancelled: { label: 'Annulé', className: 'status-cancelled' },
      accepted: { label: 'Acceptée', className: 'status-accepted' },
      rejected: { label: 'Refusée', className: 'status-rejected' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100' };
    return <Badge className={`badge ${config.className}`}>{config.label}</Badge>;
  };

  const getVehicleTypeLabel = (type) => {
    const labels = {
      truck: 'Camion',
      van: 'Fourgon',
      refrigerated: 'Frigorifique',
      flatbed: 'Plateau',
      tanker: 'Citerne'
    };
    return labels[type] || type;
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-6 h-6 ${interactive ? 'cursor-pointer' : ''} ${
              star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
            }`}
            onClick={interactive ? () => onChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!mission) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Mission non trouvée</p>
        </div>
      </Layout>
    );
  }

  const mapCenter = [(mission.pickup_lat + mission.delivery_lat) / 2, (mission.pickup_lng + mission.delivery_lng) / 2];
  const canRate = mission.status === 'completed' && 
    !ratings.some(r => r.rater_id === user?.id);
  const canPay = isEnterprise && mission.status === 'matched' && mission.final_price;
  const canComplete = isEnterprise && mission.status === 'in_progress';

  return (
    <Layout>
      <div className="space-y-6" data-testid="mission-detail-page">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
                {mission.title}
              </h1>
              {getStatusBadge(mission.status)}
            </div>
            <p className="text-slate-500">
              Créée par {mission.enterprise_name} • {new Date(mission.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isCarrier && mission.status === 'pending' && (
              <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600" data-testid="submit-bid-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Faire une offre
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Soumettre une offre</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitBid} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Véhicule</Label>
                      <Select value={bidForm.vehicle_id} onValueChange={(v) => setBidForm({...bidForm, vehicle_id: v})}>
                        <SelectTrigger data-testid="bid-vehicle-select">
                          <SelectValue placeholder="Sélectionnez un véhicule" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicles.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.name} - {getVehicleTypeLabel(v.vehicle_type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Prix proposé (€)</Label>
                      <Input
                        type="number"
                        value={bidForm.proposed_price}
                        onChange={(e) => setBidForm({...bidForm, proposed_price: e.target.value})}
                        placeholder={`${mission.budget_min} - ${mission.budget_max}`}
                        required
                        data-testid="bid-price-input"
                      />
                      <p className="text-xs text-slate-500">
                        Commission Flitflow: 2.5% sera déduit du paiement final
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Date de livraison estimée</Label>
                      <Input
                        type="datetime-local"
                        value={bidForm.estimated_delivery}
                        onChange={(e) => setBidForm({...bidForm, estimated_delivery: e.target.value})}
                        required
                        data-testid="bid-delivery-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Message (optionnel)</Label>
                      <Textarea
                        value={bidForm.message}
                        onChange={(e) => setBidForm({...bidForm, message: e.target.value})}
                        placeholder="Décrivez votre proposition..."
                        data-testid="bid-message-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      disabled={submittingBid}
                      data-testid="confirm-bid-btn"
                    >
                      {submittingBid ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Envoyer l&apos;offre
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {canPay && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handlePayment}
                disabled={processingPayment}
                data-testid="pay-btn"
              >
                {processingPayment ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Payer {(mission.final_price * 1.06).toFixed(2)}€
              </Button>
            )}

            {canComplete && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleCompleteMission}
                data-testid="complete-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Marquer terminée
              </Button>
            )}

            {canRate && (
              <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="rate-btn">
                    <Star className="w-4 h-4 mr-2" />
                    Évaluer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Évaluer {isEnterprise ? 'le transporteur' : 'l\'entreprise'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitRating} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Note</Label>
                      <div className="flex justify-center py-2">
                        {renderStars(ratingForm.rating, true, (r) => setRatingForm({...ratingForm, rating: r}))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Commentaire (optionnel)</Label>
                      <Textarea
                        value={ratingForm.comment}
                        onChange={(e) => setRatingForm({...ratingForm, comment: e.target.value})}
                        placeholder="Partagez votre expérience..."
                        data-testid="rating-comment-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      disabled={submittingRating}
                      data-testid="submit-rating-btn"
                    >
                      {submittingRating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Envoyer l&apos;évaluation
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Info (if accepted) */}
            {mission.final_price && (
              <Card className="bg-slate-900 text-white">
                <CardContent className="p-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-slate-400 text-sm">Prix accepté</div>
                      <div className="text-2xl font-bold font-['JetBrains_Mono']">{mission.final_price}€</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Commission (6%)</div>
                      <div className="text-2xl font-bold font-['JetBrains_Mono'] text-orange-400">
                        {mission.platform_fee?.toFixed(2)}€
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">Total</div>
                      <div className="text-2xl font-bold font-['JetBrains_Mono'] text-emerald-400">
                        {(mission.final_price + (mission.platform_fee || 0)).toFixed(2)}€
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Map */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo']">Itinéraire</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80 rounded-lg overflow-hidden">
                  <MapContainer center={mapCenter} zoom={6} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap'
                    />
                    <Marker position={[mission.pickup_lat, mission.pickup_lng]}>
                      <Popup>Enlèvement: {mission.pickup_address}</Popup>
                    </Marker>
                    <Marker position={[mission.delivery_lat, mission.delivery_lng]}>
                      <Popup>Livraison: {mission.delivery_address}</Popup>
                    </Marker>
                    <Polyline 
                      positions={[[mission.pickup_lat, mission.pickup_lng], [mission.delivery_lat, mission.delivery_lng]]}
                      color="#f97316"
                      weight={3}
                      dashArray="10, 10"
                    />
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            {/* Bids */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo']">
                  Offres ({bids.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bids.length > 0 ? (
                  <div className="space-y-4">
                    {bids.map((bid) => (
                      <div 
                        key={bid.id}
                        className="p-4 border border-slate-200 rounded-lg"
                        data-testid={`bid-item-${bid.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold text-slate-900">{bid.carrier_name}</span>
                              {getStatusBadge(bid.status)}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              {renderStars(bid.carrier_rating)}
                              <span className="text-sm text-slate-500">({bid.carrier_rating?.toFixed(1) || '0.0'})</span>
                            </div>
                            <p className="text-sm text-slate-500 mb-2">
                              <Truck className="w-4 h-4 inline mr-1" />
                              {bid.vehicle_name}
                            </p>
                            {bid.message && (
                              <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">{bid.message}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(bid.estimated_delivery).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-['JetBrains_Mono'] font-bold text-xl text-slate-900">
                              {bid.proposed_price}€
                            </div>
                            {isEnterprise && bid.status === 'pending' && mission.status === 'pending' && (
                              <div className="flex gap-2 mt-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => handleAcceptBid(bid.id)}
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                  data-testid={`accept-bid-${bid.id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleRejectBid(bid.id)}
                                  data-testid={`reject-bid-${bid.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    Aucune offre pour le moment
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ratings */}
            {ratings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Chivo']">Évaluations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {ratings.map((rating) => (
                      <div key={rating.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{rating.rater_name}</span>
                          {renderStars(rating.rating)}
                        </div>
                        {rating.comment && (
                          <p className="text-sm text-slate-600">{rating.comment}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(rating.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Mission Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Chivo']">Détails</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Enlèvement</Label>
                  <div className="flex items-start gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                    <span className="text-sm text-slate-900">{mission.pickup_address}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Livraison</Label>
                  <div className="flex items-start gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
                    <span className="text-sm text-slate-900">{mission.delivery_address}</span>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Marchandise</Label>
                  <p className="text-sm text-slate-900 mt-1">{mission.cargo_type}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Poids</Label>
                    <p className="text-sm font-['JetBrains_Mono'] text-slate-900 mt-1">{mission.weight_kg} kg</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Volume</Label>
                    <p className="text-sm font-['JetBrains_Mono'] text-slate-900 mt-1">{mission.volume_m3} m³</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Véhicule requis</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Truck className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-900">{getVehicleTypeLabel(mission.required_vehicle_type)}</span>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Budget</Label>
                  <p className="text-xl font-['JetBrains_Mono'] font-bold text-slate-900 mt-1">
                    {mission.budget_min}€ - {mission.budget_max}€
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Date limite</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-['JetBrains_Mono'] text-slate-900">
                      {new Date(mission.deadline).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {mission.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-['Chivo']">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{mission.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MissionDetailPage;
